import logger from '../lib/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Search, Phone, Mail, MessageCircle, Calendar,
  X, ChevronRight, Clock, User, Building, UserCheck,
  TrendingUp, Users, MapPin, FileText, PhoneCall, Tag, Briefcase, Edit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import EntityDocuments from '../components/EntityDocuments';

const CLIENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'investor', label: 'Investor' },
  { value: 'nri', label: 'NRI' },
  { value: 'luxury_buyer', label: 'Luxury Buyer' },
];

const CLIENT_SOURCES = [
  { value: 'all', label: 'All Sources' },
  { value: '99acres', label: '99acres' },
  { value: 'magicbricks', label: 'MagicBricks' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'direct', label: 'Direct' },
];

const REQUIREMENT_TYPES = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'plot', label: 'Plot' },
  { value: 'commercial', label: 'Commercial' },
];

const CLIENT_TAGS = [
  'Hot Buyer', 'Investor', 'NRI', 'Luxury', 'Repeat Client'
];

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, deals_in_progress: 0, closed_deals: 0 });
  const [clientNotes, setClientNotes] = useState([]);
  const [clientActivities, setClientActivities] = useState([]);
  const [newNote, setNewNote] = useState('');
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', client_type: 'buyer',
    budget: '', budget_max: '', requirement: '', preferred_location: '',
    source: 'direct', assigned_to: '', tags: [], notes: '',
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchClients();
    fetchStats();
    if (isAdmin) fetchAgents();
  }, [user]);

  useEffect(() => {
    filterClientsData();
  }, [clients, searchTerm, filterType, filterSource, filterAgent]);

  const fetchAgents = async () => {
    try {
      const response = await api.get('/team/agents');
      setAgents(response.data.filter(a => a.role === 'sales_rep'));
    } catch (error) {
      logger.error('Failed to fetch agents');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/clients/stats/summary');
      setStats(response.data);
    } catch (error) {
      logger.error('Failed to fetch client stats');
    }
  };

  const filterClientsData = useCallback(() => {
    let filtered = clients;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(term) ||
        c.phone?.includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.requirement?.toLowerCase().includes(term) ||
        c.preferred_location?.toLowerCase().includes(term)
      );
    }
    if (filterType !== 'all') filtered = filtered.filter(c => c.client_type === filterType);
    if (filterSource !== 'all') filtered = filtered.filter(c => c.source === filterSource);
    if (filterAgent !== 'all') {
      if (filterAgent === 'unassigned') filtered = filtered.filter(c => !c.assigned_to);
      else filtered = filtered.filter(c => c.assigned_to === filterAgent);
    }
    setFilteredClients(filtered);
  }, [clients, searchTerm, filterType, filterSource, filterAgent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, tags: formData.tags || [] };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (isEditMode && selectedClient) {
        await api.put(`/clients/${selectedClient.id}`, payload);
        toast.success('Client updated!');
      } else {
        await api.post('/clients', payload);
        toast.success('Client created!');
      }
      setIsModalOpen(false);
      setIsEditMode(false);
      resetForm();
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleAssign = async (clientId, agentId) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) return;
      await api.put(`/clients/${clientId}`, { ...client, assigned_to: agentId === 'unassigned' ? null : agentId });
      toast.success('Client assigned!');
      fetchClients();
    } catch (error) {
      toast.error('Failed to assign client');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedClient) return;
    try {
      await api.post(`/clients/${selectedClient.id}/notes?content=${encodeURIComponent(newNote)}`);
      toast.success('Note added!');
      setNewNote('');
      fetchClientNotes(selectedClient.id);
      fetchClientActivities(selectedClient.id);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleLogCall = async () => {
    if (!selectedClient) return;
    try {
      await api.post(`/clients/${selectedClient.id}/log-call`);
      toast.success('Call logged!');
      fetchClientActivities(selectedClient.id);
    } catch (error) {
      toast.error('Failed to log call');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deleted');
      setIsDetailOpen(false);
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const openClientDetail = async (client) => {
    setSelectedClient(client);
    setIsDetailOpen(true);
    fetchClientNotes(client.id);
    fetchClientActivities(client.id);
  };

  const openEditModal = (client) => {
    setFormData({
      name: client.name, phone: client.phone, email: client.email || '',
      client_type: client.client_type || 'buyer', budget: client.budget || '',
      budget_max: client.budget_max || '', requirement: client.requirement || '',
      preferred_location: client.preferred_location || '', source: client.source || 'direct',
      assigned_to: client.assigned_to || '', tags: client.tags || [], notes: client.notes || '',
    });
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const fetchClientNotes = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/notes`);
      setClientNotes(response.data);
    } catch (error) {
      logger.error('Failed to fetch notes');
    }
  };

  const fetchClientActivities = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/activities`);
      setClientActivities(response.data);
    } catch (error) {
      logger.error('Failed to fetch activities');
    }
  };

  const getAgentName = (agentId) => {
    if (!agentId) return 'Unassigned';
    const agent = agents.find(a => a.id === agentId);
    return agent?.name || 'Unknown';
  };

  const resetForm = () => {
    setFormData({
      name: '', phone: '', email: '', client_type: 'buyer',
      budget: '', budget_max: '', requirement: '', preferred_location: '',
      source: 'direct', assigned_to: '', tags: [], notes: '',
    });
    setIsEditMode(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatBudget = (budget, budgetMax) => {
    if (!budget && budget !== 0) return '-';
    const formatNum = (n) => {
      const num = typeof n === 'string' ? parseFloat(n) : n;
      if (isNaN(num)) return n;
      if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
      if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
      if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
      return num.toString();
    };
    if (typeof budget === 'string' && /[A-Za-z]/.test(budget)) return `₹${budget}`;
    if (budgetMax) return `₹${formatNum(budget)} - ${formatNum(budgetMax)}`;
    return `₹${formatNum(budget)}`;
  };

  const getTypeBadge = (type) => {
    const styles = {
      buyer: 'bg-blue-50 text-blue-700 border-blue-200',
      seller: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      investor: 'bg-amber-50 text-amber-700 border-amber-200',
      nri: 'bg-purple-50 text-purple-700 border-purple-200',
      luxury_buyer: 'bg-rose-50 text-rose-700 border-rose-200',
    };
    const labels = {
      buyer: 'Buyer', seller: 'Seller', investor: 'Investor',
      nri: 'NRI', luxury_buyer: 'Luxury Buyer',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${styles[type] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const getSourceBadge = (source) => {
    const styles = {
      '99acres': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'magicbricks': 'bg-amber-50 text-amber-700 border-amber-200',
      'referral': 'bg-teal-50 text-teal-700 border-teal-200',
      'website': 'bg-cyan-50 text-cyan-700 border-cyan-200',
      'direct': 'bg-slate-50 text-slate-600 border-slate-200',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[source] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
        {source}
      </span>
    );
  };

  const getActivityIcon = (type) => {
    const icons = {
      call: <PhoneCall className="w-3 h-3 text-green-500" />,
      note_added: <FileText className="w-3 h-3 text-blue-500" />,
      converted: <UserCheck className="w-3 h-3 text-purple-500" />,
      meeting: <Calendar className="w-3 h-3 text-orange-500" />,
      whatsapp: <MessageCircle className="w-3 h-3 text-green-600" />,
    };
    return icons[type] || <Clock className="w-3 h-3 text-slate-400" />;
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  return (
    <Layout>
      <div className="aavaas-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="aavaas-page-title" data-testid="clients-title">Clients Management</h1>
            <p className="aavaas-page-subtitle">Manage and track all your real estate clients</p>
          </div>
          <Dialog open={isModalOpen} onOpenChange={(open) => { setIsModalOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="aavaas-btn-primary" data-testid="add-client-button">
                <Plus className="w-4 h-4 mr-1.5" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{isEditMode ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="client-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Client Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="client-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="client-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="client-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="client_type">Client Type</Label>
                    <Select value={formData.client_type} onValueChange={(value) => setFormData({ ...formData, client_type: value })}>
                      <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CLIENT_TYPES.filter(t => t.value !== 'all').map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="budget">Budget (₹)</Label>
                    <Input id="budget" value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="e.g., 80L or 1.2Cr" data-testid="client-budget-input" />
                  </div>
                  <div>
                    <Label htmlFor="requirement">Requirement Type</Label>
                    <Select value={formData.requirement || 'none'} onValueChange={(value) => setFormData({ ...formData, requirement: value === 'none' ? '' : value })}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {REQUIREMENT_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="preferred_location">Preferred Location</Label>
                    <Input id="preferred_location" value={formData.preferred_location} onChange={(e) => setFormData({ ...formData, preferred_location: e.target.value })} placeholder="e.g., Mohali, Chandigarh" />
                  </div>
                  <div>
                    <Label htmlFor="source">Source</Label>
                    <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CLIENT_SOURCES.filter(s => s.value !== 'all').map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && (
                    <div>
                      <Label>Assigned Agent</Label>
                      <Select value={formData.assigned_to || 'none'} onValueChange={(value) => setFormData({ ...formData, assigned_to: value === 'none' ? '' : value })}>
                        <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                {/* Tags */}
                <div>
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {CLIENT_TAGS.map(tag => (
                      <button key={tag} type="button" onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${formData.tags.includes(tag) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." rows={3} />
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="ghost" onClick={() => { setIsModalOpen(false); resetForm(); }}>Cancel</Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-client-button">
                    {isEditMode ? 'Update Client' : 'Create Client'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white border-0 shadow-sm" data-testid="stat-total-clients">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg"><Users className="w-5 h-5 text-slate-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-xs text-slate-500">Total Clients</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm" data-testid="stat-active-clients">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
                <p className="text-xs text-slate-500">Active Clients</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm" data-testid="stat-deals-progress">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><Briefcase className="w-5 h-5 text-orange-600" /></div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.deals_in_progress}</p>
                <p className="text-xs text-slate-500">Deals in Progress</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm" data-testid="stat-closed-deals">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><Building className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.closed_deals}</p>
                <p className="text-xs text-slate-500">Closed Deals</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by client name, phone, email, or requirement..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-0" data-testid="search-clients-input" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] bg-slate-50 border-0" data-testid="filter-client-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-[140px] bg-slate-50 border-0" data-testid="filter-client-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger className="w-[150px] bg-slate-50 border-0"><SelectValue placeholder="All Agents" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {(filterType !== 'all' || filterSource !== 'all' || filterAgent !== 'all' || searchTerm) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterType('all'); setFilterSource('all'); setFilterAgent('all'); setSearchTerm(''); }}>
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Requirement</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Budget</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Source</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assigned</th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Last Activity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date Added</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 10 : 9} className="px-4 py-12 text-center text-slate-500">
                      <UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No clients found</p>
                      <p className="text-sm">Add your first client or convert a lead to get started</p>
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => openClientDetail(client)} data-testid={`client-row-${client.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <span className="font-medium text-slate-900">{client.name}</span>
                            {client.tags && client.tags.length > 0 && (
                              <div className="flex gap-1 mt-0.5">
                                {client.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-600">{client.phone}</div>
                        {client.email && <div className="text-xs text-slate-400">{client.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-900 capitalize">{client.requirement || '-'}</div>
                        {client.preferred_location && <div className="text-xs text-slate-500">{client.preferred_location}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{formatBudget(client.budget, client.budget_max)}</td>
                      <td className="px-4 py-3">{getTypeBadge(client.client_type)}</td>
                      <td className="px-4 py-3">{getSourceBadge(client.source)}</td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-sm text-slate-600" onClick={(e) => e.stopPropagation()}>
                          <Select value={client.assigned_to || 'unassigned'} onValueChange={(value) => handleAssign(client.id, value)}>
                            <SelectTrigger className="w-[130px] h-8 text-xs">
                              <SelectValue>{getAgentName(client.assigned_to)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-slate-500">{formatDate(client.last_activity)}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{formatDate(client.created_at)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEditModal(client)} title="Edit" data-testid={`edit-client-${client.id}`}>
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { window.location.href = `tel:${client.phone}`; }} title="Call">
                            <Phone className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { const a = document.createElement('a'); a.href = `https://wa.me/${client.phone.replace(/\D/g, '')}`; a.target = '_blank'; a.rel = 'noopener noreferrer'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }} title="WhatsApp">
                            <MessageCircle className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openClientDetail(client)} title="View Details">
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Client Detail Panel */}
        <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
            {selectedClient && (
              <>
                <SheetHeader className="mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <SheetTitle className="text-xl">{selectedClient.name}</SheetTitle>
                      <div className="flex items-center gap-2 mt-2">
                        {getTypeBadge(selectedClient.client_type)}
                        {getSourceBadge(selectedClient.source)}
                        {selectedClient.converted_from_lead_id && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Converted from Lead</span>
                        )}
                      </div>
                    </div>
                  </div>
                </SheetHeader>

                <div className="space-y-6">
                  {/* Contact Info */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Contact Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <a href={`tel:${selectedClient.phone}`} className="text-blue-600 hover:underline">{selectedClient.phone}</a>
                      </div>
                      {selectedClient.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${selectedClient.email}`} className="text-blue-600 hover:underline">{selectedClient.email}</a>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Client Details */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Client Details</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500">Type:</span>
                        <p className="font-medium capitalize">{selectedClient.client_type?.replace('_', ' ') || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Budget:</span>
                        <p className="font-medium">{formatBudget(selectedClient.budget, selectedClient.budget_max)}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Requirement:</span>
                        <p className="font-medium capitalize">{selectedClient.requirement || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Location:</span>
                        <p className="font-medium">{selectedClient.preferred_location || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Source:</span>
                        <p className="font-medium capitalize">{selectedClient.source || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Agent:</span>
                        <p className="font-medium">{getAgentName(selectedClient.assigned_to)}</p>
                      </div>
                    </div>
                    {/* Tags */}
                    {selectedClient.tags && selectedClient.tags.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-slate-500 text-sm">Tags:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedClient.tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 font-medium">{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedClient.notes && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-slate-500 text-sm">Notes:</span>
                        <p className="text-sm mt-1 whitespace-pre-line">{selectedClient.notes}</p>
                      </div>
                    )}
                  </Card>

                  {/* Quick Actions */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Quick Actions</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" className="justify-start" onClick={() => { window.location.href = `tel:${selectedClient.phone}`; }}>
                        <PhoneCall className="w-4 h-4 mr-2 text-green-600" /> Call Client
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={() => { const a = document.createElement('a'); a.href = `https://wa.me/${selectedClient.phone.replace(/\D/g, '')}`; a.target = '_blank'; a.rel = 'noopener noreferrer'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }}>
                        <MessageCircle className="w-4 h-4 mr-2 text-green-500" /> WhatsApp
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={handleLogCall}>
                        <Phone className="w-4 h-4 mr-2 text-blue-600" /> Log Call
                      </Button>
                      <Button variant="outline" className="justify-start" onClick={() => { openEditModal(selectedClient); setIsDetailOpen(false); }}>
                        <Edit className="w-4 h-4 mr-2 text-slate-600" /> Edit Client
                      </Button>
                    </div>
                  </Card>

                  {/* Notes */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Notes</h3>
                    <div className="flex gap-2 mb-3">
                      <Textarea placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={2} className="flex-1" data-testid="client-note-input" />
                      <Button onClick={handleAddNote} disabled={!newNote.trim()} data-testid="add-client-note-button">Add</Button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {clientNotes.length === 0 ? (
                        <p className="text-sm text-slate-500">No notes yet</p>
                      ) : (
                        clientNotes.map(note => (
                          <div key={note.id} className="bg-slate-50 rounded p-2 text-sm">
                            <p>{note.content}</p>
                            <p className="text-xs text-slate-400 mt-1">{note.created_by_name} - {formatDate(note.created_at)}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>

                  {/* Documents */}
                  <Card className="p-4">
                    <EntityDocuments entityType="client" entityId={selectedClient.id} entityName={selectedClient.name} />
                  </Card>

                  {/* Activity Timeline */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Activity Timeline</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {clientActivities.length === 0 ? (
                        <p className="text-sm text-slate-500">No activity yet</p>
                      ) : (
                        clientActivities.map(activity => (
                          <div key={activity.id} className="flex gap-3 text-sm">
                            <div className="mt-1">{getActivityIcon(activity.activity_type)}</div>
                            <div>
                              <p className="text-slate-700">{activity.description}</p>
                              <p className="text-xs text-slate-400">{activity.created_by_name} - {formatDate(activity.created_at)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>

                  {/* Danger Zone */}
                  {isAdmin && (
                    <Card className="p-4 border-red-200">
                      <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 w-full" onClick={() => handleDelete(selectedClient.id)} data-testid="delete-client-button">
                        Delete Client
                      </Button>
                    </Card>
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
};

export default Clients;
